import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { API, graphqlOperation } from "aws-amplify";
import Table from "./table/Table";
import Loading from "_shared/components/Loading/Loading";
import { usdCurrency } from "_shared/helpers/formatting";
import { useQuery } from "@tanstack/react-query";

//Table styles
const Styles = styled.div`
  table {
    border-collapse: collapse;
    height: calc(100vh - 320px);
    white-space: nowrap;
  }
  td {
    color: #ffffff;
    padding: 10px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
  }
  td:nth-child(1),
  td:nth-child(2),
  td:nth-child(3) {
    text-align: left;
  }
  table::-webkit-scrollbar {
    height: 15px;
    width: 15px;
    position: fixed;
    background-color: #999999;
    border: 4px solid #2e2e2e;
  }

  table::-webkit-scrollbar-thumb {
    height: 15px;
    border-radius: 10px;
    background-color: #ffffff;
  }
  .pagination {
    color: #ffffff;
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: center;
    margin-top: 20px;
  }
  th {
    transition: background-color 0.25s ease, color 0.25s ease;
    margin-right: -12px;
    text-align: center;
  }
  th[draggable="true"]:hover {
    background-color: #666666 !important;
    color: #ffffff !important;
  }
  tr:nth-child(odd) {
    background-color: #131313;
  }
`;

const defaultColumns = [
  {
    Header: "Position",
    columns: [
      {
        Header: "Bullseye Alerts",
        accessor: "bullseye_alert",
        id: "bullseye_alert",
        Key: "bullseye_alert",
      },
      {
        Header: "Ticker",
        accessor: "ticker",
        id: "ticker",
        Key: "ticker",
      },
      {
        Header: "Last Price",
        accessor: "px_last",
        id: "px_last",
        Key: "px_last",
      },
    ],
  },
  {
    Header: "TRADING RANGE - What to do",
    columns: [
      {
        Header: "Low",
        accessor: "calc_trade_range_low",
        id: "calc_trade_range_low",
        Key: "calc_trade_range_low",
      },
      {
        Header: "Range",
        accessor: "calc_trade_range_spread",
        id: "calc_trade_range_spread",
        Key: "calc_trade_range_spread",
      },
      {
        Header: "High",
        accessor: "calc_trade_range_high",
        id: "calc_trade_range_high",
        Key: "calc_trade_range_high",
      },
      {
        Header: "Range Signal",
        accessor: "calc_trade_range_range_signal",
        id: "calc_trade_range_range_signal",
        Key: "calc_trade_range_range_signal",
      },
      {
        Header: "% from Trade Range | Low",
        accessor: "calc_trade_range_percent_low",
        id: "calc_trade_range_percent_low",
        Key: "calc_trade_range_percent_low",
      },
      {
        Header: "% from Trade Range | High",
        accessor: "calc_trade_range_percent_high",
        id: "calc_trade_range_percent_high",
        Key: "calc_trade_range_percent_high",
      },
      {
        Header: "Odds",
        accessor: "calc_trade_range_odds",
        id: "calc_trade_range_odds",
        Key: "calc_trade_range_odds",
      },
    ],
  },
  {
    Header: "LOOKING AHEAD",
    columns: [
      {
        Header: "S/T Momentum Signal",
        accessor: "calc_momentum_indicator_trade_30d_flag",
        id: "calc_momentum_indicator_trade_30d_flag",
        Key: "calc_momentum_indicator_trade_30d_flag",
      },
      {
        Header: "M/T Momentum Signal",
        accessor: "calc_momentum_indicator_trade_90d_flag",
        id: "calc_momentum_indicator_trade_90d_flag",
        Key: "calc_momentum_indicator_trade_90d_flag",
      },
    ],
  },
  {
    Header: "MONEYLINE STATS",
    columns: [
      {
        Header: "Win\n%",
        accessor: "vip_win_pct",
        id: "vip_win_pct",
        Key: "vip_win_pct",
      },
      {
        Header: "Avg %\nGain",
        accessor: "vip_avg_pct_gain",
        id: "vip_avg_pct_gain",
        Key: "vip_avg_pct_gain",
      },
      {
        Header: "Loss\n%",
        accessor: "vip_loss_pct",
        id: "vip_loss_pct",
        Key: "vip_loss_pct",
      },
      {
        Header: "Avg %\nLoss",
        accessor: "vip_avg_pct_loss",
        id: "vip_avg_pct_loss",
        Key: "vip_avg_pct_loss",
      },
    ],
  },
  {
    Header: "Volatility",
    columns: [
      {
        Header: "1D Realized\nVolatility",
        accessor: "volatility_30d_1d",
        id: "volatility_30d_1d",
        Key: "volatility_30d_1d",
      },
      {
        Header: "1D Implied\nVolatility",
        accessor: "put_imp_vol_30d_1d",
        id: "put_imp_vol_30d_1d",
        Key: "put_imp_vol_30d_1d",
      },
      {
        Header: "-5D Avg\nSh Volume",
        accessor: "volume_avg_5d",
        id: "volume_avg_5d",
        Key: "volume_avg_5d",
      },
    ],
  },
];

const momoDict = {
  NEUTRAL: "hold",
  BEARISH: "sell",
  BULLISH: "buy",
};

function VIPView(props) {
  const [columns, setColumns] = useState(defaultColumns);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  useEffect(() => {
    localStorage.setItem("view", "basic");
    if (props.tab?.section?.length > 0) {
      setColumns(
        props.tab.section
          .filter((obj) => obj.hidden === false)
          .map((obj) => {
            return {
              Header: obj.name,
              columns: obj.column
                .filter((obj2) => obj2.hidden === false)
                .map((obj3) => {
                  return {
                    id: obj3.id,
                    Header: obj3.name,
                    accessor: obj3.key,
                    Key: obj3.key,
                    Hidden: obj3.hidden,
                  };
                }),
            };
          }),
      );
      const hiddenColumns = props.tab.section
        .filter((obj) => obj.hidden === true)
        .map((obj) => {
          return {
            name: obj.name,
            id: obj.id,
          };
        });
      setHiddenColumns(hiddenColumns);
    }
  }, [props.tab]);

  const { data: alerts } = useQuery({
    queryKey: ["bullseyeAlerts"],
    queryFn: async () =>
      (
        await API.graphql({
          query: `{ panel(where: {name: {_eq: "Bullseye AI Trade Ideas"}}) { alerts } }`,
        })
      )?.data?.panel?.[0]?.alerts,
    refetchInterval: 600000, //10 minutes
  });

  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [view, setView] = React.useState(
    localStorage.getItem("vip-view") === "oversold" ? "oversold" : "overbought",
  );
  const fetchIdRef = React.useRef(0);

  const fetchData = React.useCallback(
    async ({ sortBy }) => {
      const fetchId = ++fetchIdRef.current;

      if (fetchId === fetchIdRef.current) {
        setLoading(true);
        if (sortBy) {
          if (sortBy.includes("calc_trade_range_range")) {
            sortBy = `algo_daily_CALC_TRADING_RANGE_SIGNAL:${
              sortBy.split(":")[1]
            }`;
          } else if (sortBy.includes("calc_trade_range_spread")) {
            sortBy = `px_close_1m:${sortBy.split(":")[1]}`;
          } else if (sortBy.includes("calc_")) {
            sortBy = `algo_daily_${sortBy.split(":")[0].toUpperCase()}:${
              sortBy.split(":")[1]
            }`;
          }
        }
        let isDaily = true;

        let pxCloseDates = [];
        const date = new Date();
        for (let i = 0; i < 5; i++) {
          pxCloseDates.push(date.toISOString().split("T")[0]);
          date.setDate(date.getDate() - 1);
        }
        pxCloseDates = `[${pxCloseDates.map((obj) => `"${obj}"`).join(",")}]`;

        const response = {
          data: {
            ticker: (
              await API.graphql(
                graphqlOperation(`{
            data: data3(where: {ticker: {_nilike: "%curncy"}, 
            algo_daily_30D_ST_MOMENTUM: {_neq: "${
              localStorage.getItem("vip-view") === "oversold"
                ? "BEARISH"
                : "BULLISH"
            }"}, 
            daily: {_eq: false}, px_close_dt: {_in: ${pxCloseDates}}, algo_${
                  props.tab.name === "VIP View" ? "daily_" : ""
                }CALC_TRADING_RANGE_SIGNAL: {_eq: "${
                  localStorage.getItem("vip-view") === "oversold"
                    ? "Buy"
                    : "Sell"
                }"}, algo_daily_CALC_TRADE_RANGE_ODDS: {_is_null: false}}, order_by: {${
                  sortBy ?? `ticker: desc`
                }}) {                  
                ticker
                px_volume
                px_volume_1d
                volume_avg_5d
                volume_avg_6m
                volume_avg_3m
                volume_avg_30d
                volatility_30d
                volatility_30d_1d
                volatility_30d_1w
                volatility_30d_1m
                volatility_30d_3m
                ticker_and_exch_code
                ticker
                si_percent_equity_float
                short_int_pct_chg
                security_typ
                px_last
                px_close_ytd
                px_close_dt
                px_close_5d
                px_close_3m
                px_close_1yr
                px_close_1m
                px_close_1d
                put_imp_vol_30d
                put_imp_vol_30d_1d
                put_imp_vol_30d_1w
                put_imp_vol_30d_1m
                put_imp_vol_30d_3m
                onem_short_int_pct
                name
                eqy_beta_6m: eqy_raw_beta_6m
                eqy_beta: eqy_raw_beta
                current_trr_ytd
                current_trr_5d
                current_trr_3mo
                current_trr_1mo
                current_trr_1d
                crncy
                country
                cdr_country_code
                short_int_date
                red_px_last
                red_eqy_beta: red_eqy_raw_beta
                red_eqy_beta_6m: red_eqy_raw_beta_6m
                red_si_percent_equity_float
                red_short_int_pct_chg
                red_onem_short_int_pct
                red_volatility_30d
                red_put_imp_vol_30d
                red_current_trr_1d
                red_current_trr_5d
                red_current_trr_1mo
                red_current_trr_3mo
                red_current_trr_ytd
                red_px_volume_1d
                red_volume_avg_5d
                red_volume_avg_30d
                red_volume_avg_3m
                red_volume_avg_6m
                red_px_close_1d
                red_px_close_5d
                red_px_close_1m
                red_px_close_3m
                red_px_close_1yr
                red_px_close_ytd
                calc_position_longbow_signal_strength
                insider_monthly_transaction
                algo_30D_ST_MOMENTUM
                algo_30D_ST_MOMENTUM_3D
                algo_30D_ST_PRICE
                algo_90D_MT_MOMENTUM
                algo_90D_MT_PRICE
                algo_IVOL_SIGNAL
                algo_CALC_TRADING_RANGE_SIGNAL
                algo_CALC_TRADING_RANGE_ASYMMETRY
                algo_CALC_TRADE_RANGE_LOW
                algo_CALC_TRADE_RANGE_HIGH
                algo_IVOL_1WK_CHECK
                algo_CALC_TRADE_RANGE_PERCENT_LOW
                algo_CALC_TRADE_RANGE_PERCENT_HIGH
                algo_CALC_POSITION_LONGBOW_SIGNAL
                algo_CALC_POSITION_LONGBOW_SIGNAL_STRENGTH
                algo_CALC_TRADE_RANGE_ODDS
                algo_CurrImpVolDiscPrem
                algo_CurrImpVolDiscPrem1D
                algo_CurrImpVolDiscPrem30D
                algo_CurrImpVolDiscPrem7D
                algo_CurrImpVolDiscPrem90D
                algo_daily_30D_ST_MOMENTUM
                algo_daily_30D_ST_MOMENTUM_3D
                algo_daily_30D_ST_PRICE
                algo_daily_90D_MT_MOMENTUM
                algo_daily_90D_MT_PRICE
                algo_daily_IVOL_SIGNAL
                algo_daily_CALC_TRADING_RANGE_SIGNAL
                algo_daily_CALC_TRADING_RANGE_ASYMMETRY
                algo_daily_CALC_TRADE_RANGE_LOW
                algo_daily_CALC_TRADE_RANGE_HIGH
                algo_daily_IVOL_1WK_CHECK
                algo_daily_IVOL_3MO_CHECK
                algo_daily_CALC_TRADE_RANGE_PERCENT_LOW
                algo_daily_CALC_TRADE_RANGE_PERCENT_HIGH
                algo_daily_CALC_POSITION_LONGBOW_SIGNAL
                algo_daily_CALC_POSITION_LONGBOW_SIGNAL_STRENGTH
                algo_daily_CALC_TRADE_RANGE_ODDS
                algo_daily_CurrImpVolDiscPrem
                algo_daily_CurrImpVolDiscPrem1D
                algo_daily_CurrImpVolDiscPrem30D
                algo_daily_CurrImpVolDiscPrem7D
                algo_daily_CurrImpVolDiscPrem90D
                calc_realized_volatility_current
                calc_realized_volatility_1w
                calc_realized_volatility_1m
                calc_realized_volatility_3m
                exposure
                entry_date
                entry_price
                daily
                country
                algo_eod_CALC_POSITION_LONGBOW_SIGNAL
                algo_eod_CALC_POSITION_LONGBOW_SIGNAL_STRENGTH
                algo_eod_CALC_TRADE_RANGE_ODDS
                algo_eod_PX_LAST
                algo_eod_CALC_TRADE_RANGE_LOW
                algo_eod_CALC_TRADE_RANGE_HIGH
                volume_avg_1d
                calc_volume_1d
                calc_volume_5d
                calc_volume_1m
                calc_volume_3m
                algo_daily_kiss_low
                algo_daily_kiss_high
                eqy_split_dt
                eqy_split_ratio
                moneyline_stats
            }
        }`),
              )
            )?.data?.data?.map((obj) => ({ ticker: obj.ticker, data: obj })),
          },
        };
        for (let i = 0; i < response.data.ticker.length; i++) {
          if (response.data.ticker[i].data) {
            response.data.ticker[i].data.id = response.data.ticker[i].id;

            //move alert objects to data
            response.data.ticker[i].data.alert_longbow_overall_signal =
              response.data.ticker[i].alert_longbow_overall_signal;
            response.data.ticker[i].data.alert_trading_range_signal =
              response.data.ticker[i].alert_trading_range_signal;
            response.data.ticker[i].data.alert_momentum_30d_flag =
              response.data.ticker[i].alert_momentum_30d_flag;
            response.data.ticker[i].data.alert_momentum_90d_flag =
              response.data.ticker[i].alert_momentum_90d_flag;
            response.data.ticker[i].data.alert_volatility_signal =
              response.data.ticker[i].alert_volatility_signal;

            response.data.ticker[i] = response.data.ticker[i].data;
            if (!response.data.ticker[i].security_typ) {
              response.data.ticker[i].security_typ = "US EQUITIES";
            }
          }
          //move algo columns to algo object
          response.data.ticker[i].algo = {
            data: {
              "30D_ST_MOMENTUM":
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_30D_ST_MOMENTUM`
                ],
              "30D_ST_PRICE":
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_30D_ST_PRICE`
                ],
              "90D_MT_MOMENTUM":
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_90D_MT_MOMENTUM`
                ],
              "30D_ST_MOMENTUM_3D":
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_30D_ST_MOMENTUM_3D`
                ],
              "90D_MT_PRICE":
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_90D_MT_PRICE`
                ],
              IVOL_SIGNAL:
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_IVOL_SIGNAL`
                ],
              CALC_TRADING_RANGE_SIGNAL:
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_CALC_TRADING_RANGE_SIGNAL`
                ],
              CALC_TRADING_RANGE_ASYMMETRY:
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_CALC_TRADING_RANGE_ASYMMETRY`
                ],
              CALC_TRADE_RANGE_LOW:
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_CALC_TRADE_RANGE_LOW`
                ],
              CALC_TRADE_RANGE_HIGH:
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_CALC_TRADE_RANGE_HIGH`
                ],
              IVOL_1WK_CHECK:
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_IVOL_1WK_CHECK`
                ],
              IVOL_3MO_CHECK:
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_IVOL_3MO_CHECK`
                ],
              CALC_TRADE_RANGE_PERCENT_LOW:
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_CALC_TRADE_RANGE_PERCENT_LOW`
                ],
              CALC_TRADE_RANGE_PERCENT_HIGH:
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_CALC_TRADE_RANGE_PERCENT_HIGH`
                ],
              CALC_POSITION_LONGBOW_SIGNAL:
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_CALC_POSITION_LONGBOW_SIGNAL`
                ],
              CALC_POSITION_LONGBOW_SIGNAL_STRENGTH:
                response.data.ticker[i][
                  `algo${
                    isDaily ? `_daily` : ``
                  }_CALC_POSITION_LONGBOW_SIGNAL_STRENGTH`
                ],
              CALC_TRADE_RANGE_ODDS:
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_CALC_TRADE_RANGE_ODDS`
                ],
              CurrImpVolDiscPrem:
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_CurrImpVolDiscPrem`
                ],
              CurrImpVolDiscPrem1D:
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_CurrImpVolDiscPrem1D`
                ],
              CurrImpVolDiscPrem7D:
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_CurrImpVolDiscPrem7D`
                ],
              CurrImpVolDiscPrem30D:
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_CurrImpVolDiscPrem30D`
                ],
              CurrImpVolDiscPrem90D:
                response.data.ticker[i][
                  `algo${isDaily ? `_daily` : ``}_CurrImpVolDiscPrem90D`
                ],
            },
          };
        }
        for (const obj of response.data.ticker) {
          obj["red_calc_realized_volatility_current"] =
            obj["calc_realized_volatility_current"] < 0;
          obj["red_calc_realized_volatility_1w"] =
            obj["calc_realized_volatility_1w"] < 0;
          obj["red_calc_realized_volatility_1m"] =
            obj["calc_realized_volatility_1m"] < 0;
          obj["red_calc_realized_volatility_3m"] =
            obj["calc_realized_volatility_3m"] < 0;

          for (const key of Object.keys(obj)) {
            if (
              [
                "px_last",
                "px_close_1d",
                "calc_trade_range_low",
                "calc_trade_range_high",
              ].includes(key) &&
              obj[key]
            ) {
              obj[key] = usdCurrency(obj[key]);
            }
            if (
              [
                "current_trr_1d",
                "current_trr_5d",
                "current_trr_1mo",
                "current_trr_3m",
                "current_trr_ytd",
                "si_percent_equity_float",
                "short_int_pct_chg",
                "onem_short_int_pct",
              ].includes(key) &&
              obj[key]
            ) {
              obj[key] = `${obj[key].toFixed(2)}%`;
            }
            if (
              [
                "calc_volume_1d",
                "calc_volume_5d",
                "calc_volume_1m",
                "calc_volume_3m",
              ].includes(key)
            ) {
              const computedValue = obj[key] * 100;
              obj[key] = `${computedValue.toFixed(2)}%`;
              obj[`red_${key}`] = computedValue < 0;
            }
            if (["eqy_beta"].includes(key)) {
              obj[key] = parseFloat(obj[key]).toFixed(2);
              if (obj[key] === "NaN") {
                obj[key] = null;
              }
            }
          }

          obj.winPct =
            obj.moneyline_stats?.[
              `tr_${obj.algo_daily_CALC_TRADING_RANGE_SIGNAL.toLowerCase()}`
            ]?.[`st_${momoDict[obj.algo_daily_30D_ST_MOMENTUM]}`]?.[
              `mt_${momoDict[obj.algo_daily_90D_MT_MOMENTUM]}`
            ]?.[`d0`]?.[`win_pct`];
        }
        setLoading(false);

        // If Bullseye panel, filter out tickers with less than $10,000,000 of -5D Avg Sh Volume * Last Price
        const filteredResponse =
          props.tab.name === "VIP View"
            ? response.data.ticker.map((obj) => ({ ...obj, alerts }))
            : response.data.ticker
                .filter(
                  (obj) =>
                    obj.volume_avg_5d * parseFloat(obj.px_last) > 10000000 &&
                    (obj.winPct >= 0.7 || obj.winPct === undefined) &&
                    Object.keys(obj.moneyline_stats).length,
                )
                .reverse()
                .map((obj) => ({ ...obj, alerts }));

        // In the event that we need to manually sort the range view
        if (sortBy?.includes("px_close_1m")) {
          const [asc, desc] =
            sortBy.split(":")[1] === " desc" ? [1, -1] : [-1, 1];
          const calcOdds = (ticker) => {
            const low =
              ((ticker.px_last - ticker.algo_daily_CALC_TRADE_RANGE_LOW) /
                ticker.px_last) *
              -1;
            const high =
              (ticker.px_last - ticker.algo_daily_CALC_TRADE_RANGE_HIGH) /
              ticker.px_last;
            const result = low / (low + high);
            return result;
          };
          setData(
            filteredResponse.sort((a, b) =>
              calcOdds(a) < calcOdds(b) ? asc : desc,
            ),
          );
        } else {
          setData(filteredResponse.reverse());
        }
      }
    },
    [alerts],
  );

  const setRecords = React.useCallback(async (newData) => {
    setData(newData);
  }, []);

  return (
    <div>
      <div className="dashboard-content">
        <Styles>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              margin: "10px 0px",
            }}
          >
            {["oversold", "overbought"].map((key) => (
              <>
                <div
                  style={{
                    marginLeft: 20,
                    cursor: "pointer",
                    userSelect: "none",
                    color: "#fff",
                  }}
                  onClick={() => {
                    setView(key);
                    localStorage.setItem("vip-view", key);
                    setLoading(true);
                    setTimeout(() => fetchData({}), 250);
                  }}
                >
                  <input
                    style={{ marginRight: 7.5 }}
                    type="checkbox"
                    checked={view === key}
                  />
                  <span>{key === "oversold" ? "Buys" : "Sells"}</span>
                </div>
                <div
                  style={{
                    color: "white",
                    position: "relative",
                  }}
                  className="info-icon compact"
                  data-tip={
                    key === "overbought"
                      ? "An overbought view of your dashboard."
                      : key === "oversold"
                      ? "An oversold view of your dashboard."
                      : ""
                  }
                >
                  i
                </div>
              </>
            ))}
          </div>
          <Table
            columns={columns}
            data={data}
            fetchData={fetchData}
            loading={loading}
            pageCount={1}
            props={props}
            setRecords={setRecords}
            setRefreshKiss={() => {}}
            kiss={props.kiss}
            planInterval={props.planInterval}
            setPlanInterval={props.setPlanInterval}
            alertsInterval={props.alertsInterval}
            setLoading={setLoading}
          />
          <div
            style={{
              padding: 0,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-start",
              fontSize: 12,
            }}
          >
            {hiddenColumns.length > 0 && (
              <div
                style={{
                  color: "#75CE41",
                  padding: 10,
                  textAlign: "center",
                  width: 125,
                  fontWeight: "bold",
                }}
              >
                SEE MORE:
              </div>
            )}
            {hiddenColumns.map((obj) => (
              <div
                onClick={async () => {
                  setLoading(true);
                  await API.graphql(
                    graphqlOperation(
                      `mutation {update_section_by_pk(pk_columns: {id: "${obj.id}"}, _set: {hidden: false}) {id}}`,
                    ),
                  );
                  //refresh the table
                  props.fetchPanels();
                  setTimeout(() => {
                    setLoading(false);
                  }, 2000);
                }}
                style={{
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  color: "#ffffff",
                  padding: 5,
                  textAlign: "center",
                  borderStyle: "solid",
                  borderColor: "#ffffff",
                  borderWidth: 1,
                  textTransform: "uppercase",
                  width: 200,
                  marginRight: 20,
                }}
                key={obj.id}
              >
                <div
                  style={{
                    marginLeft: 10,
                    textAlign: "center",
                    width: "80%",
                  }}
                >
                  {obj.name}
                </div>
                <div
                  style={{
                    marginRight: 5,
                    backgroundColor: "#75CE41",
                    borderRadius: 8,
                    height: 16,
                    width: 16,
                    color: "#000000",
                    fontSize: 20,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    paddingTop: 3,
                  }}
                >
                  +
                </div>
              </div>
            ))}
          </div>
        </Styles>
      </div>
      {loading && <Loading message={"Loading..."} />}
    </div>
  );
}

export default React.memo(VIPView);
